import { supabase } from "@/shared/lib/supabaseClient";
import type { Database, LivroStatus } from "@/shared/types/database.types";

export type Livro = Database["public"]["Tables"]["livros"]["Row"];
type LivroInsert = Database["public"]["Tables"]["livros"]["Insert"];
type LivroUpdate = Database["public"]["Tables"]["livros"]["Update"];

/**
 * Livro com as categorias já resolvidas via JOIN (UX-06, migration 0038/0039):
 * um livro pode ter zero ou mais categorias — antes era só uma
 * (`categoria_id`), o que forçava escolhas artificiais. `categorias_nomes` é
 * uma versão já concatenada (", ") pronta para exibição simples em linha.
 */
export interface LivroComCategoria extends Livro {
  categorias: Array<{ id: string; nome: string }>;
  categorias_nomes: string;
}

export interface DadosLivro {
  nomeLivro: string;
  isbn?: string;
  autor?: string;
  /** Número da etiqueta do livro físico; opcional, único por igreja entre os livros não excluídos. */
  numeroCadastro?: number | null;
  categoriaIds?: string[];
  quantidadeTotal: number;
  descricao?: string;
  status: LivroStatus;
}

type LivroBruto = Livro & {
  livros_categorias: Array<{ categorias: { id: string; nome: string } | null }> | null;
};

function resolverCategorias(livro: LivroBruto): LivroComCategoria {
  const categorias = (livro.livros_categorias ?? [])
    .map((lc) => lc.categorias)
    .filter((c): c is { id: string; nome: string } => !!c)
    .sort((a, b) => a.nome.localeCompare(b.nome));
  const { livros_categorias: _omitido, ...resto } = livro;
  return {
    ...resto,
    categorias,
    categorias_nomes: categorias.map((c) => c.nome).join(", "),
  };
}

/** Substitui todos os vínculos de categoria do livro pelos ids informados. */
async function sincronizarCategorias(livroId: string, categoriaIds: string[]): Promise<void> {
  const { error: erroDelete } = await supabase
    .from("livros_categorias")
    .delete()
    .eq("livro_id", livroId);
  if (erroDelete) throw new Error("Não foi possível atualizar as categorias do livro.");
  if (!categoriaIds.length) return;
  const { error: erroInsert } = await supabase
    .from("livros_categorias")
    .insert(categoriaIds.map((categoriaId) => ({ livro_id: livroId, categoria_id: categoriaId })));
  if (erroInsert) throw new Error("Não foi possível atualizar as categorias do livro.");
}

/** Duas regras de unicidade podem barrar o salvamento: ISBN e número de cadastro. */
function mensagemDuplicado(mensagemDoBanco: string, outro: boolean): string {
  if (mensagemDoBanco.includes("livros_numero_cadastro_unico")) {
    return "Já existe outro livro com esse número de cadastro. Use um número diferente.";
  }
  return outro ? "Já existe outro livro cadastrado com esse ISBN." : "Já existe um livro cadastrado com esse ISBN.";
}

const SELECT_COM_CATEGORIAS = "*, livros_categorias(categorias(id, nome))";

export const livrosService = {
  /**
   * Lista livros não excluídos (deleted_at is null), com as categorias já
   * resolvidas via JOIN. Usado tanto pelo admin (gestão) quanto,
   * futuramente, pela tela de livros disponíveis do usuário comum (Fase
   * 2.6), com filtros adicionais aplicados ali.
   */
  async listarTodos(): Promise<LivroComCategoria[]> {
    const { data, error } = await supabase
      .from("livros")
      .select(SELECT_COM_CATEGORIAS)
      .is("deleted_at", null)
      .order("nome_livro", { ascending: true });

    if (error) throw new Error("Não foi possível carregar a lista de livros.");
    return ((data ?? []) as unknown as LivroBruto[]).map(resolverCategorias);
  },

  /**
   * Lista apenas livros disponíveis para empréstimo (status='Disponivel' e
   * quantidade_disponivel > 0) — usado pela tela "Livros Disponíveis" do
   * usuário comum. Mesma regra de negócio do sistema legado.
   */
  async listarDisponiveis(): Promise<LivroComCategoria[]> {
    const { data, error } = await supabase
      .from("livros")
      .select(SELECT_COM_CATEGORIAS)
      .is("deleted_at", null)
      .eq("status", "Disponivel")
      .gt("quantidade_disponivel", 0)
      .order("nome_livro", { ascending: true });

    if (error) throw new Error("Não foi possível carregar os livros disponíveis.");
    return ((data ?? []) as unknown as LivroBruto[]).map(resolverCategorias);
  },

  /**
   * Catálogo completo para o usuário comum: todos os livros não excluídos,
   * incluindo indisponíveis. A solicitação é bloqueada no frontend para
   * livros sem estoque, mas eles permanecem visíveis no catálogo.
   */
  async listarCatalogo(): Promise<LivroComCategoria[]> {
    const { data, error } = await supabase
      .from("livros")
      .select(SELECT_COM_CATEGORIAS)
      .is("deleted_at", null)
      .neq("status", "Excluido")
      .order("nome_livro", { ascending: true });

    if (error) throw new Error("Não foi possível carregar o catálogo de livros.");
    return ((data ?? []) as unknown as LivroBruto[]).map(resolverCategorias);
  },

  /**
   * Maior número de cadastro já usado na igreja, contando também os livros excluídos (assim o
   * número de um livro excluído não volta a ser sugerido). Nulo se nenhum livro tem número.
   */
  async maiorNumeroCadastro(): Promise<number | null> {
    const { data, error } = await supabase
      .from("livros")
      .select("numero_cadastro")
      .not("numero_cadastro", "is", null)
      .order("numero_cadastro", { ascending: false })
      .limit(1);
    if (error) throw new Error("Não foi possível consultar o último número de cadastro.");
    return data?.[0]?.numero_cadastro ?? null;
  },

  async criar(dados: DadosLivro): Promise<void> {
    const novoLivro: LivroInsert = {
      nome_livro: dados.nomeLivro,
      isbn: dados.isbn || null,
      autor: dados.autor || null,
      numero_cadastro: dados.numeroCadastro ?? null,
      quantidade_total: dados.quantidadeTotal,
      quantidade_disponivel: dados.quantidadeTotal,
      descricao: dados.descricao || null,
      status: dados.status,
    };

    const { data, error } = await supabase.from("livros").insert(novoLivro).select("id").single();

    if (error || !data) {
      if (error?.code === "23505") throw new Error(mensagemDuplicado(error.message, false));
      throw new Error("Não foi possível cadastrar o livro.");
    }

    await sincronizarCategorias(data.id, dados.categoriaIds ?? []);
  },

  /**
   * Atualiza dados do livro.
   *
   * CORREÇÃO DE BUG CRÍTICO: antes, a quantidade disponível era recalculada
   * só pela diferença aritmética entre o total antigo e o novo — sem checar
   * quantos empréstimos estavam realmente ativos. Isso permitia reduzir a
   * quantidade total a 0 mesmo com um empréstimo em aberto, e depois, ao
   * aumentar de novo, o sistema "esquecia" desse empréstimo e liberava o
   * exemplar para outro usuário — mesmo o livro ainda estando fisicamente
   * emprestado.
   *
   * Agora: conta quantos empréstimos EM_ABERTO/ATRASADO existem para este
   * livro, bloqueia reduzir o total abaixo desse número, e sempre recalcula
   * `quantidade_disponivel = novo_total - emprestimos_ativos` (em vez de
   * aritmética incremental sobre um valor que pode já estar inconsistente).
   */
  async atualizar(id: string, dados: DadosLivro, _livroAtual: Livro): Promise<void> {
    const { count: emprestimosAtivos, error: errorContagem } = await supabase
      .from("emprestimos")
      .select("id", { count: "exact", head: true })
      .eq("livro_id", id)
      .in("status", ["EM_ABERTO", "ATRASADO"]);

    if (errorContagem) {
      throw new Error("Não foi possível verificar empréstimos ativos deste livro.");
    }

    const totalEmprestado = emprestimosAtivos ?? 0;

    if (dados.quantidadeTotal < totalEmprestado) {
      throw new Error(
        `Não é possível reduzir para ${dados.quantidadeTotal} exemplar(es): há ${totalEmprestado} empréstimo(s) em andamento com este livro.`,
      );
    }

    const novaQuantidadeDisponivel = dados.quantidadeTotal - totalEmprestado;

    const atualizacao: LivroUpdate = {
      nome_livro: dados.nomeLivro,
      isbn: dados.isbn || null,
      autor: dados.autor || null,
      numero_cadastro: dados.numeroCadastro ?? null,
      quantidade_total: dados.quantidadeTotal,
      quantidade_disponivel: novaQuantidadeDisponivel,
      descricao: dados.descricao || null,
      status: dados.status,
    };

    const { error } = await supabase.from("livros").update(atualizacao).eq("id", id);

    if (error) {
      if (error.code === "23505") throw new Error(mensagemDuplicado(error.message, true));
      throw new Error("Não foi possível atualizar o livro.");
    }

    await sincronizarCategorias(id, dados.categoriaIds ?? []);
  },

  /**
   * Soft delete: bloqueado se houver qualquer empréstimo em curso
   * (PENDENTE, EM_ABERTO ou ATRASADO) para este livro — mesma regra de
   * proteção que existia no sistema legado.
   */
  async excluir(id: string): Promise<void> {
    const { count, error: errorEmprestimos } = await supabase
      .from("emprestimos")
      .select("id", { count: "exact", head: true })
      .eq("livro_id", id)
      .in("status", ["PENDENTE", "EM_ABERTO", "ATRASADO"]);

    if (errorEmprestimos) {
      throw new Error("Não foi possível verificar empréstimos associados a este livro.");
    }

    if (count && count > 0) {
      throw new Error(
        "Este livro não pode ser excluído: há empréstimos em curso ou pendentes associados a ele.",
      );
    }

    const { error } = await supabase
      .from("livros")
      .update({ deleted_at: new Date().toISOString(), status: "Excluido" })
      .eq("id", id);

    if (error) throw new Error("Não foi possível excluir o livro.");
  },
};
